/**
 * Storico delle revisioni contrattuali, per tipologia e per fornitore.
 *
 * Nessuna entità dedicata (PROGETTO.md §5): per tipologia sono le gare della tipologia con l'esito
 * dell'analisi approvata, più i contratti con la loro catena; per fornitore sono le sue offerte
 * (l'ultima revisione per gara) con l'esito, più i suoi contratti.
 */

import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import {
  analisiApi,
  anagraficheApi,
  fornitoriApi,
} from "../services/acquistiApi";
import { Campo, Scelta, Caricamento, Vuoto, Avviso } from "../components/campi";
import { STATO_CONTRATTO } from "../constants/optionSet";
import { dataIt, euro, etichetta } from "../utils/formato";

function TabellaContratti({ contratti, conFornitore }) {
  if (!contratti.length)
    return <p className="text-sm text-gray-500 p-4">Nessun contratto.</p>;
  const perId = new Map(contratti.map((c) => [String(c.id).toLowerCase(), c]));
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Contratto</th>
          {conFornitore && <th>Fornitore</th>}
          <th>Periodo</th>
          <th>Stato</th>
          <th className="text-right">Importo annuo</th>
          <th>Sostituisce</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 text-sm">
        {contratti.map((c) => {
          const prec = c.contrattoPrecedenteId
            ? perId.get(String(c.contrattoPrecedenteId).toLowerCase())
            : null;
          return (
            <tr key={c.id}>
              <td>
                <Link
                  to={`/contratti/${c.id}`}
                  className="text-acquisti-700 hover:underline"
                >
                  {c.nome}
                </Link>
                {c.garaOrigineId && (
                  <span className="badge-info ml-2">da gara</span>
                )}
              </td>
              {conFornitore && <td>{c.fornitore || "—"}</td>}
              <td className="whitespace-nowrap">
                {dataIt(c.dataDecorrenza)} → {dataIt(c.dataFine)}
              </td>
              <td>{etichetta(STATO_CONTRATTO, c.stato)}</td>
              <td className="text-right">{euro(c.importoAnnuo)}</td>
              <td>
                {c.contrattoPrecedenteId ? (
                  <Link
                    to={`/contratti/${c.contrattoPrecedenteId}`}
                    className="text-acquisti-700 hover:underline"
                  >
                    {prec?.nome || "contratto precedente"}
                  </Link>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function StoricoPage() {
  const [params, setParams] = useSearchParams();
  const { canAccessSection } = useAuth();
  const asse = params.get("per") === "fornitore" ? "fornitore" : "tipologia";
  const scelta = params.get("id") || "";

  const [tipologie, setTipologie] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [dati, setDati] = useState(null);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    anagraficheApi
      .tipologieFornitura()
      .then((r) => setTipologie(r.value || []))
      .catch(() => {});
    if (canAccessSection("acquisti_fornitori")) {
      fornitoriApi
        .list()
        .then((r) => setFornitori(r.value || []))
        .catch(() => {});
    }
  }, [canAccessSection]);

  useEffect(() => {
    if (!scelta) {
      setDati(null);
      return;
    }
    setCaricamento(true);
    const p =
      asse === "fornitore"
        ? analisiApi.storicoFornitore(scelta)
        : analisiApi.storicoTipologia(scelta);
    p.then((d) => {
      setDati(d);
      setErrore(null);
    })
      .catch((e) => setErrore(e.message))
      .finally(() => setCaricamento(false));
  }, [asse, scelta]);

  const elenco = asse === "fornitore" ? fornitori : tipologie;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-gray-900">Storico</h1>
      <div className="card !p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <Campo label="Per">
          <Scelta
            valore={asse}
            onChange={(v) => setParams({ per: v })}
            opzioni={[
              { value: "tipologia", label: "Tipologia di fornitura" },
              ...(fornitori.length
                ? [{ value: "fornitore", label: "Fornitore" }]
                : []),
            ]}
            vuoto={null}
          />
        </Campo>
        <Campo
          label={asse === "fornitore" ? "Fornitore" : "Tipologia"}
          className="sm:col-span-2"
        >
          <Scelta
            valore={scelta}
            onChange={(v) => setParams({ per: asse, id: v })}
            opzioni={elenco.map((x) => ({ value: x.id, label: x.nome }))}
            vuoto="scegli…"
          />
        </Campo>
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {caricamento && <Caricamento />}
      {!caricamento && !dati && <Vuoto titolo="Scegli cosa guardare" />}

      {!caricamento && dati && asse === "tipologia" && (
        <>
          <section className="card !p-0 overflow-x-auto">
            <h2 className="font-semibold text-gray-900 p-4 pb-2">Gare</h2>
            {dati.gare.length === 0 ? (
              <p className="text-sm text-gray-500 p-4 pt-0">
                Nessuna gara per questa tipologia.
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Gara</th>
                    <th>Capitolato</th>
                    <th>Stato</th>
                    <th>Inviata</th>
                    <th>Aggiudicata a</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {dati.gare.map((g) => (
                    <tr key={g.id}>
                      <td>
                        <Link
                          to={`/gare/${g.id}/comparazione`}
                          className="text-acquisti-700 hover:underline"
                        >
                          {g.codice} {g.nome}
                        </Link>
                      </td>
                      <td>{g.capitolato || "—"}</td>
                      <td>{g.statoLabel}</td>
                      <td>
                        {g.dataInvio
                          ? new Date(g.dataInvio).toLocaleDateString("it-IT")
                          : "—"}
                      </td>
                      <td>
                        {g.esito
                          ? `${g.esito.fornitore} (${g.esito.punteggio})`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <section className="card !p-0 overflow-x-auto">
            <h2 className="font-semibold text-gray-900 p-4 pb-2">Contratti</h2>
            <TabellaContratti contratti={dati.contratti} conFornitore />
          </section>
        </>
      )}

      {!caricamento && dati && asse === "fornitore" && (
        <>
          <section className="card !p-0 overflow-x-auto">
            <h2 className="font-semibold text-gray-900 p-4 pb-2">Offerte</h2>
            {dati.offerte.length === 0 ? (
              <p className="text-sm text-gray-500 p-4 pt-0">
                Il fornitore non ha partecipato a gare.
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Gara</th>
                    <th>Stato gara</th>
                    <th className="text-center">Rev.</th>
                    <th className="text-right">Importo</th>
                    <th>Punteggio</th>
                    <th>Esito</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {dati.offerte.map((o) => (
                    <tr key={o.offertaId}>
                      <td>
                        {o.gara ? (
                          <Link
                            to={`/gare/${o.gara.id}/comparazione`}
                            className="text-acquisti-700 hover:underline"
                          >
                            {o.gara.codice} {o.gara.nome}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{o.gara?.statoLabel || "—"}</td>
                      <td className="text-center">{o.revisione}</td>
                      <td className="text-right">{euro(o.importoTotale)}</td>
                      <td>{o.punteggio ?? "—"}</td>
                      <td>
                        {o.vinta === true ? (
                          <span className="badge-success">vinta</span>
                        ) : o.vinta === false ? (
                          <span className="badge-gray">non aggiudicata</span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <section className="card !p-0 overflow-x-auto">
            <h2 className="font-semibold text-gray-900 p-4 pb-2">Contratti</h2>
            <TabellaContratti contratti={dati.contratti} />
          </section>
        </>
      )}
    </div>
  );
}
